import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Box } from '@mui/material';

import { usePhoneScreen } from '@/designs-dev/device/PhoneScreenContext';
import StatusBar from '@/designs-dev/device/StatusBar';
import { useHideTabBar } from '@/designs-dev/device/TabBarVisibility';

const SCREEN_W = 375; // PhoneFrame screen width
const SWIPE_EDGE = 40; // gesture must start within this many px of the left edge
const SWIPE_THRESHOLD = 80; // drag past this to commit the back
const SETTLE_MS = 240;
// With a still bar, the bar goes first and the page follows: each half of the
// bar's fade (out, then in), and then the body slides. Back runs it the other
// way round: the body leaves, then the bar swaps back.
const HALF = 140;
const BAR_MS = HALF * 2;

// A pushed (stack) page that slides in from the right over the current screen,
// like a native push — the BottomSheet's animation pattern, horizontal. The
// underlying screen stays rendered beneath, so the iOS edge-swipe back (same
// gesture as SignUpFlow) reveals it as you drag. Children get `close()`, which
// animates the page out and then calls `onClose` — `onClose` is the single
// pop action, shared by the back button and the gesture.
const PushScreen = ({
  onClose,
  bar,
  children,
}: {
  onClose: () => void;
  /**
   * The page's app bar, kept still while the body slides: the bar underneath
   * fades out, then this one fades in, so the bar feels fixed and only its
   * contents change, the way a native push does. The status bar is drawn here,
   * so leave it out. Without `bar` the whole page slides, bar and all.
   */
  bar?: (close: () => void) => ReactNode;
  children: (close: () => void) => ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(true), []);
  // The tab bar shouldn't show behind (or under) a pushed page.
  useHideTabBar();

  const closing = useRef(false);
  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    // With a still bar, wait for its fade-out to finish too.
    window.setTimeout(onClose, (bar ? SETTLE_MS + BAR_MS : SETTLE_MS) + 40);
  };

  // Edge-swipe back (same gesture as SignUpFlow): drag rightward from the
  // left edge; mostly-vertical drags are abandoned so scrolling still works.
  const rootRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if (closing.current) return;
    // Not from a control. Capturing the pointer for a swipe steals the click,
    // so a back button sitting in the edge strip never fired.
    if ((e.target as HTMLElement).closest('button, a, [role="button"]')) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect || e.clientX - rect.left > SWIPE_EDGE) return;
    dragStart.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dy) > Math.abs(dx) + 12) {
      // mostly vertical — let it scroll, abandon the swipe
      dragStart.current = null;
      setDragging(false);
      setDragX(0);
      return;
    }
    setDragX(Math.max(0, Math.min(dx, SCREEN_W)));
  };
  const endDrag = () => {
    if (!dragStart.current) return;
    dragStart.current = null;
    setDragging(false); // enable the settle transition
    if (dragX > SWIPE_THRESHOLD) {
      closing.current = true;
      setDragX(SCREEN_W); // slide the rest of the way off
      window.setTimeout(onClose, SETTLE_MS);
    } else {
      setDragX(0); // spring back
    }
  };

  const transform =
    dragging || dragX > 0
      ? `translateX(${dragX}px)`
      : open
        ? 'translateX(0)'
        : 'translateX(100%)';

  // Portal onto the phone screen (like BottomSheet) so a pushed page covers
  // everything — including the tab bar.
  const screenEl = usePhoneScreen();

  // How far in the page is, 0 to 1, for the bar's fade. Follows a swipe too.
  const shown = dragging || dragX > 0 ? 1 - dragX / SCREEN_W : open ? 1 : 0;

  if (bar) {
    const split = (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 15,
          display: 'flex',
          flexDirection: 'column',
          // Nothing here paints: the bar fades in and the body slides in,
          // and the screen underneath shows through until they do.
          pointerEvents: 'none',
        }}
      >
        {/* The status bar is the same on both screens, so it simply stays. */}
        <Box sx={{ flexShrink: 0, bgcolor: 'background.default' }}>
          <StatusBar />
        </Box>
        {/* Two halves, not a cross-fade: the bar underneath fades out to
            plain Gardenia first, then this one fades in. Closing runs it
            backwards. A swipe drives both from the finger. */}
        <Box
          sx={{ flexShrink: 0, position: 'relative', pointerEvents: 'auto' }}
        >
          <Box
            sx={{
              position: 'absolute',
              // Stops short of the bar's bottom line, so the line stays put
              // while the contents change above it.
              top: 0,
              left: 0,
              right: 0,
              bottom: '1px',
              bgcolor: 'background.default',
              opacity: dragging ? Math.min(1, shown * 2) : shown,
              transition: dragging
                ? 'none'
                : `opacity ${HALF}ms ease ${open ? 0 : SETTLE_MS + HALF}ms`,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              opacity: dragging ? Math.max(0, shown * 2 - 1) : shown,
              transition: dragging
                ? 'none'
                : `opacity ${HALF}ms ease ${open ? HALF : SETTLE_MS}ms`,
            }}
          >
            {bar(close)}
          </Box>
        </Box>
        <Box
          ref={rootRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            transform,
            // After the bar has settled on the way in; straight away on the
            // way out.
            transition: dragging
              ? 'none'
              : `transform ${SETTLE_MS}ms ease ${open ? BAR_MS : 0}ms`,
            pointerEvents: 'auto',
          }}
        >
          {children(close)}
        </Box>
      </Box>
    );
    return screenEl ? createPortal(split, screenEl) : split;
  }

  const page = (
    <Box
      ref={rootRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.default',
        zIndex: 15,
        transform,
        transition: dragging ? 'none' : `transform ${SETTLE_MS}ms ease`,
      }}
    >
      {children(close)}
    </Box>
  );

  return screenEl ? createPortal(page, screenEl) : page;
};

export default PushScreen;
